<template>
  <section
    id="history"
    ref="timelineRef"
    class="history landing-container overflow-hidden relative py-60px lt-sm:pt-60px lt-sm:pb-160px lt-sm:px-32px"
  >
    <div
      class="history-container flex flex-col items-center transition-all duration-1000"
      :class="[isTimelineVisible ? 'translate-y-0 opacity-100' : 'translate-y-50px opacity-0']"
    >
      <h2
        class="history-title pf-sc-semibold font-semibold font-s-36px text-#000000 lh-60px text-center mb-4px lt-sm:font-s-48px lt-sm:lh-60px"
      >
        发展历程
      </h2>
      <p
        class="history-subtitle pf-sc-regular font-s-16px text-main/70 text-center lh-30px mb-50px lt-sm:font-s-24px lt-sm:lh-40px lt-sm:mb-88px"
      >
        从探索到引领，绘家科技每一步都坚实有力
      </p>

      <div class="history-top w-full h-255px flex py-50px items-center lt-sm:h-176px">
        <div
          class="history-year-wrapper relative pl-127px w-600px shrink-0 h-170px overflow-hidden lt-sm:hidden"
        >
          <Transition name="year-flow">
            <div
              :key="currentYearData?.year"
              class="history-year-content absolute left-127px top-0 h-full flex items-center w-full"
            >
              <h3
                class="history-year-text pf-sc-bold text-170px font-bold text-primary select-none leading-none"
              >
                {{ currentYearData?.year }}
              </h3>
            </div>
          </Transition>
        </div>

        <div
          class="history-event-wrapper flex-1 h-full relative overflow-hidden pt-14px lt-sm:h-176px lt-sm:pt-0"
        >
          <Transition name="event-fade" mode="out-in">
            <div
              :key="currentYearData?.year + '-' + currentEventIndex"
              class="history-event-content absolute left-0 top-14px w-full flex flex-col pr-114px lt-sm:pr-0 lt-sm:static"
            >
              <div
                class="history-event-header flex justify-between items-center w-full lt-sm:h-45px"
              >
                <span
                  class="history-event-month pf-sc-semibold font-s-22px font-semibold lh-30.8px text-main flex-1 lt-sm:font-s-32px lt-sm:lh-30.8px"
                >
                  {{ currentEventData?.month }}
                </span>
                <div
                  v-if="(currentYearData?.events?.length ?? 0) > 1"
                  class="history-event-controls flex gap-12px relative z-10 lt-sm:gap-16px"
                >
                  <i
                    class="i-custom-arrow-circle-left history-event-arrow wh-32px transition-all lt-sm:wh-45px"
                    :class="[
                      currentEventIndex === 0
                        ? 'opacity-30 cursor-not-allowed'
                        : 'cursor-pointer hover:i-custom-arrow-circle-left-active hover:scale-110 active:scale-95',
                    ]"
                    @click="prevEvent"
                  ></i>
                  <i
                    class="i-custom-arrow-circle-right history-event-arrow wh-32px transition-all lt-sm:wh-45px"
                    :class="[
                      currentEventIndex === (currentYearData?.events?.length ?? 0) - 1
                        ? 'opacity-30 cursor-not-allowed'
                        : 'cursor-pointer hover:i-custom-arrow-circle-right-active hover:scale-110 active:scale-95',
                    ]"
                    @click="nextEvent"
                  ></i>
                </div>
              </div>
              <div
                class="history-event-line h-2px w-34px bg-primary mt-11px origin-left lt-sm:h-4px lt-sm:w-68px lt-sm:mt-25px"
              ></div>
              <div
                class="history-event-desc pf-sc-regular w-full font-s-20px lh-35px text-main/70 mt-26px lt-sm:font-s-24px lt-sm:mt-26px lt-sm:lh-normal"
              >
                {{ currentEventData?.content }}
              </div>
            </div>
          </Transition>
        </div>
      </div>

      <div
        class="history-timeline w-full relative pt-47px pb-60px lt-sm:py-0 lt-sm:mt-70px lt-sm:hidden"
      >
        <div
          class="history-timeline-track relative h-16px w-full bg-white rounded-full px-60px lt-sm:h-16px lt-sm:px-60px"
        >
          <div
            class="history-timeline-progress absolute left-0 top-0 h-full bg-gradient-to-r from-[#60A5FA] to-[#2563EB] rounded-full transition-all duration-500 ease-out z-1"
            :style="progressStyle"
          ></div>

          <div class="history-timeline-nodes relative h-full w-full">
            <div
              v-for="(item, index) in historyYears"
              :key="item.year"
              class="history-timeline-node absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex flex-col items-center cursor-pointer group z-2"
              :style="getTimelineNodePositionStyle(index)"
              @click="selectYear(index)"
            >
              <div
                class="history-timeline-dot rounded-full transition-all duration-300 relative bg-white lt-sm:wh-14px"
                :class="getTimelineDotClasses(index)"
                :style="getTimelineDotStyle(index)"
              ></div>
              <span
                class="history-timeline-year absolute top-24px text-16px transition-all duration-300 whitespace-nowrap"
                :class="getTimelineYearTextClasses(index)"
              >
                {{ item.year }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        class="history-timeline-mobile lt-sm:flex hidden flex items-center justify-between w-full px-0"
      >
        <i
          class="i-custom-button-previous-mobile history-mobile-btn shrink-0 cursor-pointer transition-all absolute lt-sm:wh-64px z-10 left-0"
          :class="[
            currentYearIndex === 0
              ? 'cursor-not-allowed i-custom-button-previous-mobile-disabled'
              : 'active:i-custom-button-previous-mobile-active',
          ]"
          @click="prevYear"
        ></i>

        <div
          class="history-mobile-track-container timeline-mask relative flex-1 overflow-hidden h-100px flex items-center"
        >
          <div
            class="history-mobile-track-line absolute left-0 right-0 bg-white rounded-full lt-sm:h-16px"
          ></div>
          <TransitionGroup
            :name="'timeline-' + slideDirection"
            tag="div"
            class="history-mobile-nodes hidden relative w-full h-16px z-1 lt-sm:px-70px lt-sm:h-16px lt-sm:block"
          >
            <div
              v-for="yearIndex in mobileTimelineIndices"
              :key="yearIndex"
              class="history-mobile-node absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex flex-col items-center cursor-pointer lt-sm:w-60px"
              :style="getMobileNodeStyle(yearIndex)"
              :class="{ invisible: yearIndex < 0 || yearIndex >= historyYears.length }"
              @click="yearIndex >= 0 && yearIndex < historyYears.length && selectYear(yearIndex)"
            >
              <div
                class="history-mobile-dot rounded-full bg-white transition-all z-2 border-primary border-1 lt-sm:wh-18px"
                :class="{ active: yearIndex === currentYearIndex }"
              ></div>
              <div
                class="history-mobile-year hidden absolute text-main/60 pf-sc-regular transition-all whitespace-nowrap lt-sm:font-s-22px lt-sm:top-58px lt-sm:block"
                :class="{ active: yearIndex === currentYearIndex }"
              >
                {{ historyYears[yearIndex]?.year }}
              </div>
            </div>
          </TransitionGroup>
        </div>

        <i
          class="i-custom-button-next-mobile history-mobile-btn shrink-0 cursor-pointer transition-all absolute lt-sm:wh-64px z-10 right-0"
          :class="[
            currentYearIndex === historyYears.length - 1
              ? 'cursor-not-allowed i-custom-button-next-mobile-disabled'
              : 'active:i-custom-button-next-mobile-active',
          ]"
          @click="nextYear"
        ></i>
      </div>

      <div class="history-nav flex gap-24px mt-45px lt-sm:hidden">
        <i
          class="i-custom-button-previous history-nav-btn wh-56px flex-center cursor-pointer"
          :class="{
            'opacity-30 cursor-not-allowed hover:i-custom-button-previous!': currentYearIndex === 0,
          }"
          @click="prevYear"
        ></i>
        <i
          class="i-custom-button-next history-nav-btn wh-56px flex-center cursor-pointer"
          :class="{
            'opacity-30 cursor-not-allowed hover:i-custom-button-next!':
              currentYearIndex === historyYears.length - 1,
          }"
          @click="nextYear"
        ></i>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { historyYears } from '@/constants/common'

  const timelineRef = ref<HTMLElement | null>(null)
  const { isVisible: isTimelineVisible } = useScrollReveal(timelineRef)

  const currentYearIndex = ref(historyYears.length - 1)
  const currentEventIndex = ref(0)
  const slideDirection = ref<'next' | 'prev'>('next')

  const currentYearData = computed(() => {
    const data = historyYears[currentYearIndex.value]
    return data || historyYears[0]
  })
  const currentEventData = computed(() => {
    const events = currentYearData.value?.events || []
    return events[currentEventIndex.value] || events[0]
  })

  const mobileTimelineIndices = computed(() => {
    const total = historyYears.length
    if (total <= 0) return []
    const prev = currentYearIndex.value - 1
    const curr = currentYearIndex.value
    const next = currentYearIndex.value + 1
    return [prev, curr, next]
  })

  const progressStyle = computed(() => {
    const totalSteps = Math.max(historyYears.length - 1, 1)
    if (currentYearIndex.value === totalSteps) {
      return {
        width: '100%',
      }
    }
    const percentage = currentYearIndex.value / totalSteps
    return {
      width: `calc(60px + ${percentage} * (100% - 120px))`,
    }
  })

  const nextYear = () => {
    if (currentYearIndex.value < historyYears.length - 1) {
      slideDirection.value = 'next'
      currentYearIndex.value++
      currentEventIndex.value = 0
    }
  }

  const prevYear = () => {
    if (currentYearIndex.value > 0) {
      slideDirection.value = 'prev'
      currentYearIndex.value--
      currentEventIndex.value = 0
    }
  }

  const nextEvent = () => {
    const events = currentYearData.value?.events || []
    if (currentEventIndex.value < events.length - 1) {
      currentEventIndex.value++
    }
  }

  const prevEvent = () => {
    if (currentEventIndex.value > 0) {
      currentEventIndex.value--
    }
  }

  const selectYear = (index: number) => {
    slideDirection.value = index > currentYearIndex.value ? 'next' : 'prev'
    currentYearIndex.value = index
    currentEventIndex.value = 0
  }

  const getTimelineNodePositionStyle = (index: number) => {
    return { left: `${(index / (historyYears.length - 1)) * 100}%` }
  }

  const getTimelineDotClasses = (index: number) => {
    return [
      index < currentYearIndex.value
        ? 'wh-13px'
        : 'wh-16px border-primary group-hover:border-[#2563EB]  border-1',
      index === currentYearIndex.value
        ? 'wh-16px scale-150 outline-6px outline-#CEE0FF outline-solid border-none'
        : '',
    ]
  }

  const getTimelineDotStyle = (index: number) => {
    if (index === currentYearIndex.value) {
      return { background: 'linear-gradient(90deg, #779EFF 0%, #0A50FF 100%)' }
    }
    if (index < currentYearIndex.value) {
      return { background: '#FFFFFF' }
    }
    return {}
  }

  const getTimelineYearTextClasses = (index: number) => {
    return [
      index === currentYearIndex.value
        ? 'text-#2563EB font-bold pf-sc-bold scale-110'
        : 'text-#94A3B8 group-hover:text-[#64748B]',
    ]
  }

  const getMobileNodeStyle = (index: number) => {
    const diff = index - currentYearIndex.value
    if (diff === -1) return { left: '70px' }
    if (diff === 0) return { left: '50%' }
    if (diff === 1) return { left: 'calc(100% - 70px)' }
    if (diff < -1) return { left: '-30px' }
    return { left: 'calc(100% + 30px)' }
  }
</script>

<style scoped>
  .history {
    background-size: cover;
    background-position: center;
    background-image: url('@/assets/images/history-bg.jpg');
  }

  @screen lt-sm {
    .history {
      background-image: url('@/assets/images/history-bg-mobile.jpg');
    }
  }

  .history-year-text {
    -webkit-text-stroke: 1px #2563eb;
    text-shadow: 0 0 20px rgba(37, 99, 235, 0.1);
  }

  .history-mobile-node {
    transition:
      left 0.5s ease,
      opacity 0.5s ease;
    will-change: left, opacity;
  }

  .history-mobile-node.invisible {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }

  .history-mobile-dot.active {
    @apply scale-150 outline-#CEE0FF outline-solid border-none bg-[linear-gradient(90deg,#779EFF_0%,#0A50FF_100%)];
    @apply lt-sm:wh-22px lt-sm:outline-8px;
  }

  .history-mobile-year.active {
    @apply text-primary font-semibold scale-110;
  }

  .timeline-mask {
    mask-image: linear-gradient(
      to right,
      transparent 0%,
      #000 15%,
      #000 85%,
      transparent 100%
    );
    -webkit-mask-image: linear-gradient(
      to right,
      transparent 0%,
      #000 15%,
      #000 85%,
      transparent 100%
    );
  }

  .year-flow-enter-active,
  .year-flow-leave-active {
    transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .year-flow-enter-from {
    opacity: 0;
    transform: translateY(100%) scale(0.9);
  }

  .year-flow-leave-to {
    opacity: 0;
    transform: translateY(-100%) scale(0.9);
  }

  .event-fade-enter-active,
  .event-fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .event-fade-enter-from,
  .event-fade-leave-to {
    opacity: 0;
  }

  .timeline-next-move,
  .timeline-prev-move {
    transition: left 0.5s ease;
  }

  .timeline-next-enter-active,
  .timeline-next-leave-active,
  .timeline-prev-enter-active,
  .timeline-prev-leave-active {
    transition:
      left 0.5s ease,
      opacity 0.5s ease;
  }

  .timeline-next-enter-from {
    opacity: 0;
    left: calc(100% + 30px);
  }

  .timeline-next-leave-to {
    opacity: 0;
    left: -30px;
  }

  .timeline-prev-enter-from {
    opacity: 0;
    left: -30px;
  }

  .timeline-prev-leave-to {
    opacity: 0;
    left: calc(100% + 30px);
  }

  .history-timeline-dot::after,
  .history-timeline-dot::before {
    content: '';
    position: absolute;
    inset: -16px;
    border-radius: 999px;
    background: transparent;
  }
</style>
